import type { ArraySchema } from '@colyseus/schema'
import type {
  ItemId,
  PlayerState,
  Projectile,
  ProjectileKind,
  RideId,
  Trap,
  TrapKind,
  World,
  WorldSnapshot,
} from '@runkr/sim'
import { PlatformSchema, ProjectileSchema, TrapSchema } from './entities'
import { PLAYER_BOOL_KEYS, PLAYER_NUMBER_KEYS } from './PlayerSchema'
import type { PlayerSchema } from './PlayerSchema'
import type { RaceState } from './RaceState'

/**
 * World ↔ `RaceState` mapping shared by server and client (PHASE1 T1.6). Pure functions over
 * sim *types*; no sim logic.
 *
 * The wire carries every player, projectile, trap, platform and coin field, the tick and crate
 * availability. It does not carry the rng streams, bot memory, `nextId` or crate respawn
 * ticks (server-only: they would reveal bots and future crate rolls), so `toWorldSnapshot`
 * takes those from a `base` snapshot (DECISIONS O-15).
 */

const PROJECTILE_KEYS = [
  'id',
  'kind',
  'owner',
  'x',
  'y',
  'w',
  'h',
  'vx',
  'vy',
  'grounded',
  'target',
  'locked',
  'expiresAtTick',
] as const satisfies readonly (keyof Projectile & keyof ProjectileSchema)[]

const TRAP_KEYS = [
  'id',
  'kind',
  'owner',
  'x',
  'y',
  'w',
  'h',
  'vy',
  'grounded',
  'ownerSafeUntil',
  'expiresAtTick',
] as const satisfies readonly (keyof Trap & keyof TrapSchema)[]

const PLATFORM_KEYS = ['x', 'y', 'fallAt', 'backAt'] as const

/** Assign `keys` from `src` to `dst`, touching only fields whose value differs. */
function copyChanged<S extends object, D extends object>(
  src: S,
  dst: D,
  keys: readonly (keyof S & keyof D)[],
): void {
  const s = src as Record<PropertyKey, unknown>
  const d = dst as Record<PropertyKey, unknown>
  for (const k of keys) {
    if (d[k] !== s[k]) d[k] = s[k]
  }
}

function setChanged<T>(arr: ArraySchema<T>, i: number, value: T): void {
  if (i >= arr.length) arr.push(value)
  else if (arr[i] !== value) arr[i] = value
}

/** Keep `dst` as long as `src` with entries matched by position and `id`. */
function syncById<S extends { id: number }, D extends { id: number }>(
  src: readonly S[],
  dst: ArraySchema<D>,
  make: () => D,
  keys: readonly (keyof S & keyof D)[],
): void {
  for (let i = 0; i < src.length; i++) {
    const s = src[i]!
    const d = dst[i]
    if (d && d.id === s.id) {
      copyChanged(s, d, keys)
    } else {
      const fresh = make()
      copyChanged(s, fresh, keys)
      setChanged(dst, i, fresh)
    }
  }
  if (dst.length > src.length) dst.splice(src.length, dst.length - src.length)
}

/** Players of `state` by sim index. */
export function playersByIndex(state: RaceState): PlayerSchema[] {
  const out: PlayerSchema[] = []
  state.players.forEach((p) => {
    out[p.index] = p
  })
  return out
}

function writePlayer(p: PlayerState, s: PlayerSchema): void {
  if (s.index !== p.id) s.index = p.id
  copyChanged(p, s, PLAYER_NUMBER_KEYS)
  copyChanged(p, s, PLAYER_BOOL_KEYS)
  const item = p.item ?? ''
  if (s.item !== item) s.item = item
  const ride = p.rideId ?? ''
  if (s.rideId !== ride) s.rideId = ride
}

/**
 * Copy the world into the room state, assigning only fields that changed (so patches stay
 * small). `state.players` must already hold one entry per world player, matched by `index`.
 */
export function writeWorldToState(world: World, state: RaceState): void {
  if (state.tick !== world.tick) state.tick = world.tick
  const byIndex = playersByIndex(state)
  for (let i = 0; i < world.players.length; i++) {
    const s = byIndex[i]
    if (!s) throw new Error(`writeWorldToState: no PlayerSchema for index ${i}`)
    writePlayer(world.players[i]!, s)
  }
  syncById(world.projectiles, state.projectiles, () => new ProjectileSchema(), PROJECTILE_KEYS)
  syncById(world.traps, state.traps, () => new TrapSchema(), TRAP_KEYS)
  for (let i = 0; i < world.crates.length; i++) {
    setChanged(state.crateTaken, i, world.tick < world.crates[i]!.respawnAtTick)
  }
  for (let i = 0; i < world.coinsTaken.length; i++) {
    setChanged(state.coinTaken, i, world.coinsTaken[i]!)
  }
  for (let i = 0; i < world.platformState.length; i++) {
    let d = state.platformState[i]
    if (!d) {
      d = new PlatformSchema()
      state.platformState.push(d)
    }
    copyChanged(world.platformState[i]!, d, PLATFORM_KEYS)
  }
}

function readPlayer(s: PlayerSchema): PlayerState {
  const p = {
    id: s.index,
    item: s.item === '' ? null : (s.item as ItemId),
    rideId: s.rideId === '' ? null : (s.rideId as RideId),
  } as PlayerState
  copyChanged(s, p, PLAYER_NUMBER_KEYS)
  copyChanged(s, p, PLAYER_BOOL_KEYS)
  return p
}

/**
 * Rebuild a `WorldSnapshot` from the room state. Fields the wire does not carry (rng streams,
 * bot memory, `nextId`, crate respawn ticks) are copied from `base`, e.g. the client's own
 * world built from `state.seed`.
 */
export function toWorldSnapshot(state: RaceState, base: WorldSnapshot): WorldSnapshot {
  const players: PlayerState[] = []
  state.players.forEach((s) => {
    players[s.index] = readPlayer(s)
  })
  for (let i = 0; i < players.length; i++) {
    if (!players[i]) throw new Error(`toWorldSnapshot: no player with index ${i}`)
  }
  const finishOrder = players
    .filter((p) => p.placement !== 0)
    .sort((a, b) => a.placement - b.placement)
    .map((p) => p.id)
  return {
    tick: state.tick,
    players,
    projectiles: state.projectiles.map((s) => {
      const p = { kind: s.kind as ProjectileKind } as Projectile
      copyChanged(s, p, PROJECTILE_KEYS)
      return p
    }),
    traps: state.traps.map((s) => {
      const t = { kind: s.kind as TrapKind } as Trap
      copyChanged(s, t, TRAP_KEYS)
      return t
    }),
    crates: base.crates.map((c) => ({ ...c })),
    rng: {
      crates: { ...base.rng.crates },
      bots: { ...base.rng.bots },
      hazards: { ...base.rng.hazards },
    },
    finishOrder,
    platformState: state.platformState.map((s) => ({
      x: s.x,
      y: s.y,
      fallAt: s.fallAt,
      backAt: s.backAt,
    })),
    coinsTaken: state.coinTaken.map((v) => v),
    bots: base.bots.map((b) => ({ ...b })),
    nextId: base.nextId,
  }
}
